import { BaseElement } from '../../../base-element';
import type { TrialEventSummaryDto, EntryStatus, PdfStatus } from '../../../../api/types';
import type { SelectOption } from '../../../ui/dt-select';
import '../../../ui/dt-select';
import '../../../ui/dt-input';
import '../../../ui/dt-button';

export interface EntryFiltersState {
  trialId: string | null;
  status: EntryStatus | 'All';
  pdfStatus: PdfStatus | 'All';
  search: string;
}

type SelectElement = HTMLElement & {
  options: SelectOption[];
  value: string;
  disabled?: boolean;
};

type InputElement = HTMLElement & {
  value: string;
};

const statusOptions: SelectOption[] = [
  { value: 'All', label: 'All statuses' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Submitted', label: 'Submitted' },
];

const pdfStatusOptions: SelectOption[] = [
  { value: 'All', label: 'All PDF statuses' },
  { value: 'Queued', label: 'Queued' },
  { value: 'InProgress', label: 'Processing' },
  { value: 'Success', label: 'Ready' },
  { value: 'Failed', label: 'Failed' },
];

export class EntryFilters extends BaseElement {
  private _trials: TrialEventSummaryDto[] = [];
  private _fixedTrialId: string | null = null;
  private _filters: EntryFiltersState = {
    trialId: null,
    status: 'All',
    pdfStatus: 'All',
    search: '',
  };

  get trials(): TrialEventSummaryDto[] {
    return this._trials;
  }

  set trials(value: TrialEventSummaryDto[]) {
    this._trials = value;
    if (this.isConnected) {
      this.updateRender();
    }
  }

  get filters(): EntryFiltersState {
    return this._filters;
  }

  set filters(value: EntryFiltersState) {
    this._filters = value;
    if (this.isConnected) {
      this.updateRender();
    }
  }

  get fixedTrialId(): string | null {
    return this._fixedTrialId;
  }

  set fixedTrialId(value: string | null) {
    this._fixedTrialId = value;
    if (value && this._filters.trialId !== value) {
      this._filters = {
        ...this._filters,
        trialId: value,
      };
    }

    if (this.isConnected) {
      this.updateRender();
    }
  }

  connectedCallback(): void {
    this.updateRender();
  }

  private buildTrialOptions(): SelectOption[] {
    const options: SelectOption[] = [{ value: 'All', label: 'All trials' }];
    this._trials.forEach((trial) => {
      options.push({ value: trial.trialEventId, label: trial.name });
    });
    return options;
  }

  private emitFiltersChange(): void {
    this.emit('filters-change', { ...this._filters });
  }

  private updateFilter<K extends keyof EntryFiltersState>(key: K, value: EntryFiltersState[K]): void {
    this._filters = { ...this._filters, [key]: value };
    this.emitFiltersChange();
  }

  private handleTrialChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ value: string }>).detail;
    const value = detail?.value ?? 'All';
    this.updateFilter('trialId', value === 'All' ? null : value);
  };

  private handleStatusChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ value: EntryStatus | 'All' }>).detail;
    const value = detail?.value ?? 'All';
    this.updateFilter('status', value);
  };

  private handlePdfStatusChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ value: PdfStatus | 'All' }>).detail;
    const value = detail?.value ?? 'All';
    this.updateFilter('pdfStatus', value);
  };

  private handleSearchInput = (event: Event): void => {
    const detail = (event as CustomEvent<{ value: string }>).detail;
    this.updateFilter('search', detail?.value ?? '');
  };

  private handleClear = (): void => {
    this._filters = {
      trialId: this._fixedTrialId,
      status: 'All',
      pdfStatus: 'All',
      search: '',
    };
    this.updateRender();
    this.emitFiltersChange();
  };

  private updateRender(): void {
    const showTrialFilter = !this._fixedTrialId;

    this.render(`
      <section class="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-end" data-testid="entry-filters">
        ${
          showTrialFilter
            ? `
        <div class="min-w-[220px] flex-1">
          <dt-select label="Trial" placeholder="All trials" data-testid="trial-filter"></dt-select>
        </div>
        `
            : ''
        }
        <div class="min-w-[180px] flex-1">
          <dt-select label="Status" placeholder="All statuses" data-testid="status-filter"></dt-select>
        </div>
        <div class="min-w-[180px] flex-1">
          <dt-select label="PDF Status" placeholder="All PDF statuses" data-testid="pdf-filter"></dt-select>
        </div>
        <div class="min-w-[220px] flex-[2]">
          <dt-input label="Search" placeholder="Handler email or dog name" data-testid="search-input"></dt-input>
        </div>
        <div class="flex items-center">
          <dt-button variant="ghost" data-testid="clear-filters">Clear</dt-button>
        </div>
      </section>
    `);

    const trialSelect = this.$('[data-testid="trial-filter"]') as SelectElement | null;
    if (trialSelect) {
      trialSelect.options = this.buildTrialOptions();
      trialSelect.value = this._filters.trialId ?? 'All';
      trialSelect.disabled = this._trials.length === 0;
      trialSelect.addEventListener('change', this.handleTrialChange);
    }

    const statusSelect = this.$('[data-testid="status-filter"]') as SelectElement | null;
    if (statusSelect) {
      statusSelect.options = statusOptions;
      statusSelect.value = this._filters.status;
      statusSelect.addEventListener('change', this.handleStatusChange);
    }

    const pdfSelect = this.$('[data-testid="pdf-filter"]') as SelectElement | null;
    if (pdfSelect) {
      pdfSelect.options = pdfStatusOptions;
      pdfSelect.value = this._filters.pdfStatus;
      pdfSelect.addEventListener('change', this.handlePdfStatusChange);
    }

    const searchInput = this.$('[data-testid="search-input"]') as InputElement | null;
    if (searchInput) {
      searchInput.value = this._filters.search;
      searchInput.addEventListener('input', this.handleSearchInput);
    }

    const clearBtn = this.$('[data-testid="clear-filters"]');
    if (clearBtn) {
      clearBtn.addEventListener('click', this.handleClear);
    }
  }
}

customElements.define('dt-entry-filters', EntryFilters);
